import { Box, Button, TextField, Typography } from '@mui/material';
import { GridApi } from 'ag-grid-community';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { styles } from './DetailTableRangeFilter.styles';

// Condition types map 1:1 to backend FilterOperators values. Both range bounds are
// inclusive, and an exact match is emitted as a single `equal` condition rather than a
// redundant `>= x AND <= x` pair — BUD-17959 for numbers, BUD-18569 for dates.
export type RangeFilterConditionType = 'greaterThanOrEqual' | 'lessThanOrEqual' | 'equal';

export type RangeFilterModel<TFilterType extends string, TCondition> = {
  filterType: TFilterType;
  operator: 'AND';
  conditions: TCondition[];
};

export type DetailTableRangeFilterRef<TModel> = {
  setModel: (model: TModel | null) => void;
  refresh: (params: { model?: TModel | null }) => boolean;
};

type RangeFilterProps<TModel> = {
  // The filter currently applied to the column. With `enableFilterHandlers` ag-grid hands this
  // to the component as a prop rather than through the imperative setModel/refresh handle.
  model?: TModel | null;
  onModelChange: (model: TModel | null) => void;
  fromLabel?: string;
  toLabel?: string;
  equalsLabel?: string;
  showEquals?: boolean;
  [key: string]: unknown;
};

// Everything a column type has to supply to get the shared Equals / From / To panel.
type RangeFilterSpec<TFilterType extends string, TCondition, TValue> = {
  displayName: string;
  // Prefixes both the instance ids and the data-test hooks, e.g. 'date-range'.
  name: string;
  filterType: TFilterType;
  inputType: 'date' | 'number';
  helperText?: string;
  createCondition: (type: RangeFilterConditionType, value: TValue) => TCondition;
  // Renders a stored condition back into the string the input element accepts.
  readConditionInput: (condition: TCondition) => string;
  // Returns null for blank or unparseable input, so it never reaches the model.
  parseInput: (rawInput: string) => TValue | null;
  isInvertedRange: (from: TValue, to: TValue) => boolean;
};

type RangeInputs = { equals: string; from: string; to: string };

const EMPTY_INPUTS: RangeInputs = { equals: '', from: '', to: '' };

// ag-grid hands over a new model object on every render, so compare by content to tell an actual
// filter change from a re-render that would otherwise discard what the user is typing.
function serializeModel(model: unknown): string {
  return JSON.stringify(model ?? null);
}

function toInputs<TFilterType extends string, TCondition extends { type: RangeFilterConditionType }, TValue>(
  spec: RangeFilterSpec<TFilterType, TCondition, TValue>,
  model: RangeFilterModel<TFilterType, TCondition> | null,
  showEquals: boolean
): RangeInputs {
  const conditions = model?.conditions;
  const exact = conditions?.find((condition) => condition.type === 'equal');
  if (exact) {
    const exactInput = spec.readConditionInput(exact);
    // A range-only panel has no Equals input, so surface an exact match as a From/To pair.
    return showEquals ? { ...EMPTY_INPUTS, equals: exactInput } : { equals: '', from: exactInput, to: exactInput };
  }

  const from = conditions?.find((condition) => condition.type === 'greaterThanOrEqual');
  const to = conditions?.find((condition) => condition.type === 'lessThanOrEqual');
  return {
    equals: '',
    from: from ? spec.readConditionInput(from) : '',
    to: to ? spec.readConditionInput(to) : ''
  };
}

function toConditions<TFilterType extends string, TCondition extends { type: RangeFilterConditionType }, TValue>(
  spec: RangeFilterSpec<TFilterType, TCondition, TValue>,
  equals: TValue | null,
  from: TValue | null,
  to: TValue | null
): TCondition[] {
  // Collapse an exact match — an explicit Equals, or a From/To pair with identical bounds —
  // into one `equal` condition so it renders as a single filter chip.
  if (equals !== null) return [spec.createCondition('equal', equals)];
  if (from !== null && to !== null && from === to) return [spec.createCondition('equal', from)];

  const conditions: TCondition[] = [];
  if (from !== null) conditions.push(spec.createCondition('greaterThanOrEqual', from));
  if (to !== null) conditions.push(spec.createCondition('lessThanOrEqual', to));
  return conditions;
}

function toModel<TFilterType extends string, TCondition extends { type: RangeFilterConditionType }, TValue>(
  spec: RangeFilterSpec<TFilterType, TCondition, TValue>,
  inputs: RangeInputs,
  showEquals: boolean
): RangeFilterModel<TFilterType, TCondition> | null {
  const equals = showEquals ? spec.parseInput(inputs.equals) : null;
  const from = spec.parseInput(inputs.from);
  const to = spec.parseInput(inputs.to);

  const nothingSet = equals === null && from === null && to === null;
  // An inverted range can never match a row, so clear the filter instead of sending it.
  const rangeIsInverted = from !== null && to !== null && spec.isInvertedRange(from, to);
  if (nothingSet || rangeIsInverted) return null;

  return { filterType: spec.filterType, operator: 'AND', conditions: toConditions(spec, equals, from, to) };
}

function createRefHandle<TModel>(showModel: (model: TModel | null) => void): DetailTableRangeFilterRef<TModel> {
  return {
    setModel(model) {
      showModel(model);
    },
    refresh(params) {
      // An undefined model means ag-grid has no update for us — leave the inputs alone.
      if (params.model !== undefined) {
        showModel(params.model);
      }
      return true;
    }
  };
}

type RangeInputProps = Readonly<{
  id: string;
  label: string;
  value: string;
  inputType: string;
  disabled: boolean;
  dataTest: string;
  onValueChange: (value: string) => void;
}>;

function RangeInput({ id, label, value, inputType, disabled, dataTest, onValueChange }: RangeInputProps) {
  return (
    <Box sx={styles.inputGroup}>
      <Typography component="label" htmlFor={id} variant="bodySmall" sx={styles.inputLabel}>
        {label}
      </Typography>
      <TextField
        type={inputType}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onValueChange(event.target.value);
        }}
        size="small"
        inputProps={{ id, 'data-test': dataTest }}
      />
    </Box>
  );
}

export function createDetailTableRangeFilter<
  TFilterType extends string,
  TCondition extends { type: RangeFilterConditionType },
  TValue
>(spec: RangeFilterSpec<TFilterType, TCondition, TValue>) {
  type TModel = RangeFilterModel<TFilterType, TCondition>;

  let instanceCount = 0;

  const RangeFilter = forwardRef<DetailTableRangeFilterRef<TModel>, RangeFilterProps<TModel>>((props, ref) => {
    const instanceId = useRef(`${spec.name}-filter-${String(++instanceCount)}`).current;
    const { fromLabel = 'From', toLabel = 'To', equalsLabel = 'Equals', showEquals = false, model = null } = props;
    const [inputs, setInputs] = useState<RangeInputs>(() => toInputs(spec, model, showEquals));
    const [formIsDirty, setFormIsDirty] = useState(false);
    const syncedModel = useRef(serializeModel(model));

    const showModel = useCallback(
      (next: TModel | null) => {
        syncedModel.current = serializeModel(next);
        setInputs(toInputs(spec, next, showEquals));
        setFormIsDirty(false);
      },
      [showEquals]
    );

    // ag-grid reuses this panel across opens, so the applied filter has to be re-read whenever it
    // changes — reopening the column menu, switching saved views, filtering from the cell context
    // menu, or clearing elsewhere. Without this the panel always opened blank, leaving the user no
    // way to see or remove the filter that was actually narrowing the table (BUD-18569).
    useEffect(() => {
      if (serializeModel(model) === syncedModel.current) return;
      showModel(model);
    }, [model, showModel]);

    const handleInputChange = (key: keyof RangeInputs, value: string) => {
      setInputs((previous) => ({ ...previous, [key]: value }));
      setFormIsDirty(true);
    };

    useImperativeHandle(ref, () => createRefHandle(showModel));

    const closePanel = () => {
      (props.api as GridApi | undefined)?.hideColumnFilter();
    };

    const handleApply = () => {
      const nextModel = toModel(spec, inputs, showEquals);
      syncedModel.current = serializeModel(nextModel);
      props.onModelChange(nextModel);
      setFormIsDirty(false);
      closePanel();
    };

    const handleClear = () => {
      showModel(null);
      props.onModelChange(null);
      closePanel();
    };

    // Equals and the From/To range are mutually exclusive, so disable whichever the
    // user isn't currently using to make the precedence obvious.
    const equalsInUse = showEquals && inputs.equals !== '';
    const rangeInUse = inputs.from !== '' || inputs.to !== '';

    return (
      <Box sx={styles.panel}>
        <Typography component="span" variant="bodySmall" sx={styles.sectionLabel}>
          Add Filter
        </Typography>

        {showEquals && (
          <RangeInput
            id={`${instanceId}-equals`}
            label={equalsLabel}
            value={inputs.equals}
            inputType={spec.inputType}
            disabled={rangeInUse}
            dataTest={`${spec.name}-equals-input`}
            onValueChange={(value) => {
              handleInputChange('equals', value);
            }}
          />
        )}

        <RangeInput
          id={`${instanceId}-from`}
          label={fromLabel}
          value={inputs.from}
          inputType={spec.inputType}
          disabled={equalsInUse}
          dataTest={`${spec.name}-from-input`}
          onValueChange={(value) => {
            handleInputChange('from', value);
          }}
        />

        <RangeInput
          id={`${instanceId}-to`}
          label={toLabel}
          value={inputs.to}
          inputType={spec.inputType}
          disabled={equalsInUse}
          dataTest={`${spec.name}-to-input`}
          onValueChange={(value) => {
            handleInputChange('to', value);
          }}
        />

        {spec.helperText && (
          <Typography component="span" variant="bodySmall" sx={styles.helperText}>
            {spec.helperText}
          </Typography>
        )}

        <Box sx={styles.footer}>
          <Button onClick={handleClear} size="small" variant="outlined" data-test="clear-button">
            Clear Filters
          </Button>
          <Button
            onClick={handleApply}
            disabled={!formIsDirty}
            size="small"
            variant="contained"
            data-test="apply-button"
          >
            Apply
          </Button>
        </Box>
      </Box>
    );
  });

  RangeFilter.displayName = spec.displayName;
  return RangeFilter;
}
